import { useEffect, useRef, useState } from "react";
import { api, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/config";
import { Instagram, X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";

const EMBED_SRC = "https://www.instagram.com/embed.js";
const STORY_MS = 7000;
const TICK_MS = 50;

function processEmbeds() {
  if (window.instgrm?.Embeds) {
    window.instgrm.Embeds.process();
    return;
  }
  if (!document.querySelector(`script[src="${EMBED_SRC}"]`)) {
    const s = document.createElement("script");
    s.src = EMBED_SRC;
    s.async = true;
    s.onload = () => window.instgrm?.Embeds?.process();
    document.body.appendChild(s);
  }
}

export default function StoryStrip() {
  const [stories, setStories] = useState([]);
  const [active, setActive] = useState(null);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const progressRef = useRef(0);
  const touchRef = useRef(null);
  const swallowClickRef = useRef(false);

  const resetProgress = () => { progressRef.current = 0; setProgress(0); };

  useEffect(() => {
    api.get("/stories").then((r) => setStories(r.data.stories || [])).catch(() => {});
  }, []);

  const open = (i) => { setActive(i); resetProgress(); setPaused(false); };
  const close = () => setActive(null);
  const go = (dir) => {
    resetProgress();
    setActive((i) => {
      const next = i + dir;
      if (next < 0) return stories.length - 1;
      if (next >= stories.length) return null; // finished the reel
      return next;
    });
  };

  // Reprocess the Instagram embed whenever the visible story changes
  useEffect(() => {
    if (active === null) return;
    const t = setTimeout(processEmbeds, 40);
    return () => clearTimeout(t);
  }, [active]);

  // Autoplay — timestamp driven so pausing resumes exactly where it stopped
  useEffect(() => {
    if (active === null || paused) return;
    const startedAt = Date.now() - progressRef.current * STORY_MS;
    const id = setInterval(() => {
      const p = (Date.now() - startedAt) / STORY_MS;
      if (p >= 1) {
        progressRef.current = 0;
        setProgress(0);
        setActive((i) => (i + 1 >= stories.length ? null : i + 1));
      } else {
        progressRef.current = p;
        setProgress(p);
      }
    }, TICK_MS);
    return () => clearInterval(id);
  }, [active, paused, stories.length]);

  // Keyboard controls
  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line
  }, [active, stories.length]);

  if (stories.length === 0) return null;

  const story = active !== null ? stories[active] : null;

  return (
    <section data-testid="story-strip" className="py-20 md:py-28 px-6 md:px-12 bg-[#FAF6F0]">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="overline">from the field</p>
            <h2 className="font-display text-4xl md:text-5xl italic font-light mt-4 leading-tight">
              Straight From Our Guides
            </h2>
            <p className="mt-4 text-sm text-[#23170E]/65 max-w-xl leading-relaxed">
              Unfiltered clips and photos our guides post from the Serengeti crater rim, the Barranco Wall
              and everywhere in between. Tap any circle and they play one after another.
            </p>
          </div>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="story-follow-link"
            className="btn-pill btn-pill-outline self-start md:self-auto"
          >
            <Instagram size={15} /> @{INSTAGRAM_HANDLE}
          </a>
        </div>

        <div className="mt-12 flex gap-6 overflow-x-auto pb-4 story-rail">
          {stories.map((s, i) => (
            <button
              key={s.id}
              data-testid={`story-bubble-${i}`}
              onClick={() => open(i)}
              className="flex-shrink-0 w-[104px] md:w-[124px] text-center group"
            >
              <span className="block p-[3px] rounded-full bg-[linear-gradient(135deg,#A6521B,#C88B35,#23170E)] transition-transform duration-300 group-hover:scale-105">
                <span className="block p-[3px] rounded-full bg-[#FAF6F0]">
                  <span className="relative block aspect-square rounded-full overflow-hidden bg-[#23170E]">
                    {s.has_cover ? (
                      <img src={`/api/stories/${s.id}/cover`} alt={s.caption || "Guide story"} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <span className="w-full h-full flex items-center justify-center text-[#C88B35]"><Instagram size={26} /></span>
                    )}
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play size={20} className="text-white" fill="white" />
                    </span>
                  </span>
                </span>
              </span>
              <span className="block mt-3 text-xs text-[#23170E]/70 leading-snug line-clamp-2">
                {s.caption || "Field note"}
              </span>
            </button>
          ))}
        </div>
      </div>

      {story && (
        <div
          data-testid="story-viewer"
          className="fixed inset-0 z-[60] bg-[#18100A]/95 backdrop-blur-sm flex items-center justify-center p-4 touch-pan-y"
          onClick={() => { if (!swallowClickRef.current) close(); }}
          onTouchStart={(e) => {
            const t = e.touches[0];
            touchRef.current = { x: t.clientX, y: t.clientY };
            setPaused(true); // press-and-hold pauses, like Instagram
          }}
          onTouchEnd={(e) => {
            const start = touchRef.current;
            touchRef.current = null;
            setPaused(false);
            swallowClickRef.current = true;
            setTimeout(() => { swallowClickRef.current = false; }, 400);
            if (!start) return;
            const t = e.changedTouches[0];
            const dx = t.clientX - start.x;
            const dy = t.clientY - start.y;
            if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.4) {
              go(dx < 0 ? 1 : -1);          // swipe left = next, right = previous
            } else if (dy > 90) {
              close();                      // swipe down to dismiss
            } else if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
              go(t.clientX < window.innerWidth * 0.32 ? -1 : 1); // tap zones
            }
          }}
        >
          {/* Segmented progress bar */}
          <div data-testid="story-progress" className="absolute top-0 left-0 right-0 flex gap-1.5 p-3" onClick={(e) => e.stopPropagation()}>
            {stories.map((s, i) => (
              <span key={s.id} className="h-[3px] flex-1 rounded-full bg-[#FAF6F0]/25 overflow-hidden">
                <span
                  data-testid={`story-progress-fill-${i}`}
                  className="block h-full bg-[#FAF6F0]"
                  style={{
                    width: i < active ? "100%" : i === active ? `${Math.min(progress, 1) * 100}%` : "0%",
                    transition: i === active ? "width 50ms linear" : "none",
                  }}
                />
              </span>
            ))}
          </div>

          <div className="absolute top-6 right-5 flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="story-pause"
              onClick={() => setPaused((p) => !p)}
              className="text-[#FAF6F0]/80 hover:text-[#FAF6F0]"
              aria-label={paused ? "Resume" : "Pause"}
            >
              {paused ? <Play size={20} fill="currentColor" /> : <Pause size={20} fill="currentColor" />}
            </button>
            <button data-testid="story-close" onClick={close} className="text-[#FAF6F0]/80 hover:text-[#FAF6F0]" aria-label="Close">
              <X size={26} />
            </button>
          </div>

          {stories.length > 1 && (
            <>
              <button
                data-testid="story-prev"
                onClick={(e) => { e.stopPropagation(); go(-1); }}
                className="absolute left-3 md:left-8 text-[#FAF6F0]/70 hover:text-[#FAF6F0]"
                aria-label="Previous"
              ><ChevronLeft size={36} /></button>
              <button
                data-testid="story-next"
                onClick={(e) => { e.stopPropagation(); go(1); }}
                className="absolute right-3 md:right-8 text-[#FAF6F0]/70 hover:text-[#FAF6F0]"
                aria-label="Next"
              ><ChevronRight size={36} /></button>
            </>
          )}

          {/* Hovering the post pauses the reel so the clip can actually be watched */}
          <div
            className="w-full max-w-[420px] max-h-[86vh] overflow-y-auto rounded-2xl mt-6"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <blockquote
              key={story.id}
              className="instagram-media"
              data-instgrm-permalink={story.instagram_url}
              data-instgrm-version="14"
              style={{ background: "#FFF", border: 0, borderRadius: 16, margin: 0, padding: 0, width: "100%" }}
            >
              <a href={story.instagram_url} target="_blank" rel="noreferrer" className="block p-8 text-center text-[#23170E]">
                View this post on Instagram
              </a>
            </blockquote>
            {story.caption && (
              <p data-testid="story-caption" className="mt-4 text-center font-display italic text-lg text-[#FAF6F0]/90 px-4">
                {story.caption}
              </p>
            )}
          </div>
        </div>
      )}

      <style>{`
        .story-rail::-webkit-scrollbar { height: 4px; }
        .story-rail::-webkit-scrollbar-thumb { background: rgba(184,111,44,0.4); border-radius: 9999px; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </section>
  );
}
